import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const AddRecoveryModal = ({ onClose, onSuccess }) => {
  const { user, currentTenant } = useAuth();
  const [formData, setFormData] = useState({
    date: new Date()?.toISOString()?.split('T')?.[0],
    sleep_hours: '',
    sleep_quality: 'good',
    heart_rate_variability: '',
    resting_heart_rate: '',
    is_rest_day: false,
    recovery_score: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { error: insertError } = await supabase
        ?.from('recovery_insights')
        ?.insert({
          tenant_id: currentTenant?.id,
          user_id: user?.id,
          date: formData?.date,
          sleep_hours: parseFloat(formData?.sleep_hours) || null,
          sleep_quality: formData?.sleep_quality,
          heart_rate_variability: parseInt(formData?.heart_rate_variability) || null,
          resting_heart_rate: parseInt(formData?.resting_heart_rate) || null,
          is_rest_day: formData?.is_rest_day,
          recovery_score: parseInt(formData?.recovery_score) || null,
          notes: formData?.notes
        });

      if (insertError) throw insertError;
      onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to add recovery entry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">Add Recovery Entry</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Date *
              </label>
              <Input
                type="date"
                value={formData?.date}
                onChange={(e) => handleChange('date', e?.target?.value)}
                required
              />
            </div>

            {/* Recovery Score */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Recovery Score (0-100)
              </label>
              <Input
                type="number"
                min="0"
                max="100"
                value={formData?.recovery_score}
                onChange={(e) => handleChange('recovery_score', e?.target?.value)}
                placeholder="85"
              />
            </div>

            {/* Sleep Hours */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Sleep Hours
              </label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="24"
                value={formData?.sleep_hours}
                onChange={(e) => handleChange('sleep_hours', e?.target?.value)}
                placeholder="7.5"
              />
            </div>

            {/* Sleep Quality */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Sleep Quality
              </label>
              <Select
                value={formData?.sleep_quality}
                onChange={(e) => handleChange('sleep_quality', e?.target?.value)}
              >
                <option value="poor">Poor</option>
                <option value="fair">Fair</option>
                <option value="good">Good</option>
                <option value="excellent">Excellent</option>
              </Select>
            </div>

            {/* Heart Rate Variability */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Heart Rate Variability (ms)
              </label>
              <Input
                type="number"
                min="0"
                value={formData?.heart_rate_variability}
                onChange={(e) => handleChange('heart_rate_variability', e?.target?.value)}
                placeholder="65"
              />
            </div>

            {/* Resting Heart Rate */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Resting Heart Rate (bpm)
              </label>
              <Input
                type="number"
                min="0"
                value={formData?.resting_heart_rate}
                onChange={(e) => handleChange('resting_heart_rate', e?.target?.value)}
                placeholder="58"
              />
            </div>
          </div>

          {/* Rest Day Checkbox */}
          <div className="mb-6">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={formData?.is_rest_day}
                onChange={(checked) => handleChange('is_rest_day', checked)}
                id="rest-day"
              />
              <label htmlFor="rest-day" className="text-sm font-medium text-foreground cursor-pointer">
                This was a rest day
              </label>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Notes
            </label>
            <textarea
              value={formData?.notes}
              onChange={(e) => handleChange('notes', e?.target?.value)}
              placeholder="How did you feel today? Any observations about your recovery?"
              rows={4}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={loading}
              iconName={loading ? 'Loader2' : 'Save'}
              iconPosition="left"
              iconClassName={loading ? 'animate-spin' : ''}
            >
              {loading ? 'Saving...' : 'Save Entry'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRecoveryModal;